import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import '../styles/styles.css';
import { getBat, getBatById, getTipoExamen, asociarExamYBat, postBateria, deleteBateria } from '../services/api';
import { normalizeApiResponse } from '../utils/normalizeResp';
import CamposTiposExamenes from './CamposTiposExamenes';
import { confirmar } from './ConfirmModal';
import { toast } from 'react-toastify';


const GestionarBaterias = () => {
  // estados para paginacion y creacion de baterias
  const [bateriasAll, setBateriasAll] = useState([]); // fetch all baterias once
  const [batsPage, setBatsPage] = useState(1);
  const [batsPerPage] = useState(5); // client-side page size for baterias (changeable)
  const [batsTotal, setBatsTotal] = useState(0);
  const [batsTotalPages, setBatsTotalPages] = useState(1);
  const [loadingBats, setLoadingBats] = useState(false);
  const [errorBats, setErrorBats] = useState(null);
  // estado creacion bateria 
  const [nombreBateria, setNombreBateria] = useState('');
  // estados para asociacion examenes
  const [baterias, setBaterias] = useState([]);
  const [examenes, setExamenes] = useState([]);
  const [selectedBateria, setSelectedBateria] = useState('');
  const [selectedExamenes, setSelectedExamenes] = useState([]);
  // abortRef para bateria
  const batsAbortRef = useRef(null);
  // in-flightRef evita la duplicacion de llamadas concurrentes
  const batsInFlightRef = useRef(false);
  const [mostrarCampos, setMostrarCampos] = useState(false);

  // Obtener lista de baterias
  const fetchBateriasAll = useCallback(async () => {
    if (batsInFlightRef.current) return;

    if (batsAbortRef.current) {
      batsAbortRef.current.abort();
      batsAbortRef.current = null;
      batsInFlightRef.current = false;
    }

    const controller = new AbortController();
    batsAbortRef.current = controller;
    batsInFlightRef.current = true;

    setLoadingBats(true);
    setErrorBats(null);

    try {
      const raw = await getBat();
      const { data } = normalizeApiResponse(raw);

      if (controller.signal.aborted) return;

      const arr = Array.isArray(data) ? data : [];
      setBateriasAll(arr);
      setBaterias(arr);
      setBatsTotal(arr.length);
      setBatsTotalPages(Math.max(1, Math.ceil(arr.length / batsPerPage)));
      setBatsPage((p) => Math.min(p, Math.max(1, Math.ceil(arr.length / batsPerPage))));
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.message === 'canceled') return;
      console.error('Error al obtener baterías:', err);
      setErrorBats(err);
      setBateriasAll([]);
      setBaterias([]);
      setBatsTotal(0);
      setBatsTotalPages(1);
    } finally {
      batsInFlightRef.current = false;
      setLoadingBats(false);
    }
  }, [batsPerPage]);

    useEffect(() => {
    fetchBateriasAll();
    return () => {
      if (batsAbortRef.current) {
        batsAbortRef.current.abort();
        batsAbortRef.current = null;
      }
      batsInFlightRef.current = false;
    }
  }, [fetchBateriasAll]);

  // el slice visible de baterias para la pagina actual
  const visibleBaterias = useMemo(() => {
    const list = bateriasAll || [];
    const start = (batsPage - 1) * batsPerPage;
    return list.slice(start, start + batsPerPage);
  }, [bateriasAll, batsPage, batsPerPage]);

  const handleCrearBateria = async (e) => {
    e.preventDefault();
    try {
      await postBateria(nombreBateria);
      toast.success('Batería agregada correctamente.');
      fetchBateriasAll();
      setBatsPage(1);
      setNombreBateria('');
    } catch (error) {
      console.error('Error al crear batería:', error);
      if (error.response) {
        toast.error(`Error: ${error.response.data.error}`);
      } else {
        toast.error('Error al crear batería.');
      }
    }
  };

  const handleEliminarBateria = async (bateriaId, nombreBateria) => {
    const confirmed = await confirmar(
      `¿Estás segura de que quieres eliminar la batería "${nombreBateria}"?`,
      { variante: 'peligro' }
    );
    if (!confirmed) return;
    try {
      await deleteBateria(bateriaId);
      toast.success(`Batería "${nombreBateria}" eliminada correctamente.`);
      fetchBateriasAll(); // refrescar lista
    } catch (error) {
      console.error('Error al eliminar batería:', error);
      toast.error(`No se pudo eliminar la batería "${nombreBateria}".`);
    }
  };

  // Obtener exámenes asociados a la batería seleccionada
  const fetchExamenes = async (bateriaId) => {
    try {
      const allTiposResp = await getTipoExamen();
      const allTipos = allTiposResp.data || [];

      const bateriaResp = await getBatById(bateriaId);
      const bateria = bateriaResp.data;
      const asociados = bateria.tipo_examenes.map(te => te.codigo);

      setExamenes(allTipos);
      setSelectedExamenes(asociados);
    } catch (error) {
      console.error('Error al obtener tipos de examenes:', error);
      toast.error('No se pudieron cargar los exámenes de la batería.');
      setExamenes([]);
      setSelectedExamenes([]);
    }
  };

  // Manejar el cambio de batería seleccionada
  const handleBateriaChange = (e) => {
    const selectedBateriaId = e.target.value;
    setSelectedBateria(selectedBateriaId);
    fetchExamenes(selectedBateriaId);
  };

  // Manejar la selección de exámenes
  const handleExamenChange = (e) => {
    const examId = e.target.value;
    if (e.target.checked) {
      setSelectedExamenes([...selectedExamenes, examId]);
    } else {
      setSelectedExamenes(selectedExamenes.filter((id) => id !== examId));
    }
  };

  // Enviar los datos seleccionados al servidor
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { codigos: selectedExamenes };
      await asociarExamYBat(payload, selectedBateria); // api.js
      toast.success('Exámenes asociados a la batería correctamente.');
      setSelectedBateria('');
      setSelectedExamenes([]);
    } catch (error) {
      console.error('Error al asociar exámenes:', error);
      toast.error('Error al asociar exámenes.');
    }
  };

  const batsPrev = () => { if (batsPage > 1) setBatsPage((p) => p - 1); };
  const batsNext = () => { if (batsPage < batsTotalPages) setBatsPage((p) => p + 1); };


  return (
    <div className="content-wrapper">
      <h1>Gestionar Baterías</h1>
      <div className="container">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <h2 htmlFor="bateria_id">Seleccione una Batería</h2>
            <select
              name="bateria_id"
              id="bateria_id"
              className="form-control"
              value={selectedBateria}
              onChange={handleBateriaChange}
              required
            >
              <option value="">Selecciona una batería</option>
              {baterias.map((bateria) => (
                <option key={bateria.id_bat} value={bateria.id_bat}>
                  {bateria.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <h2 htmlFor="examenes">Seleccione Exámenes</h2>
            <button
                type="button"
                className='btn btn-secondary'
                onClick={() => setMostrarCampos(!mostrarCampos)}
              >
                Información exámenes
              </button>
            <div className='examenes-layout' >
              <div className='examenes-left'>
                <div className="checkbox-group">
                  {examenes && examenes.length > 0 ? (
                    examenes.map((examen) => (
                      <div className="checkbox-item" key={examen.codigo}>
                        <input
                          type="checkbox"
                          id={`examen_${examen.codigo}`}
                          value={examen.codigo}
                          checked={selectedExamenes.includes(examen.codigo)}
                          onChange={handleExamenChange}
                        />
                        <label htmlFor={`examen_${examen.codigo}`}>
                          {examen.nombre}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p>No hay exámenes disponibles.</p>
                  )}
                </div>
              </div>
              {mostrarCampos && (
                <div className='examenes-right'>
                  <CamposTiposExamenes />
                </div>
              )}
            </div>
          </div>
          <div className="form-group btn-group">
            <button
              type="submit"
              className="btn btn-submit"
              disabled={!selectedBateria || (selectedExamenes && selectedExamenes.length === 0)}
            >
              Asociar Exámenes
            </button>
          </div>
        </form>
      </div>
      <div className="container">
        <h2 className="section-title">NUEVA BATERÍA</h2>
        <form onSubmit={handleCrearBateria}>
          <div className="form-group">
            <label>Nombre de la Batería</label>
            <input type="text" className="form-control" value={nombreBateria} onChange={(e) => setNombreBateria(e.target.value)} />
          </div>
          <button type="submit" className="btn-create">Crear</button>
        </form>
      </div>
      <div className="container">
        <h2 className="section-title">Baterías</h2>
        {loadingBats ? <div>Loading baterias...</div> : null}
        {errorBats ? <div className="error">Error cargando baterías</div> : null}
        <table className="table">
          <thead>
            <tr>
              <th>Nombre de la Batería</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleBaterias.map((bateria) => (
              <tr key={bateria.id_bat}>
                <td>{bateria.nombre}</td>
                <td>
                  <button className="btn btn-danger" onClick={() => handleEliminarBateria(bateria.id_bat, bateria.nombre)}>Eliminar</button>
                </td>
              </tr>
            ))}
            {visibleBaterias.length === 0 && !loadingBats && (
              <tr>
                <td colSpan={2} style={{ textAlign: 'center' }}>No hay baterías en esta página.</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="pagination-controls">
          <button onClick={batsPrev} disabled={batsPage <= 1 || loadingBats}>Prev</button>
          <span> Página {batsPage} de {batsTotalPages} — {batsTotal} baterías</span>
          <button onClick={batsNext} disabled={batsPage >= batsTotalPages || loadingBats}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default GestionarBaterias;
